import React, { useEffect, useRef, useState } from 'react'
import { Field, useFormikContext } from 'formik'
import { get, isEmpty } from 'lodash'
import { IconChevronRight } from '@tabler/icons-react'
import classNames from 'classnames'

import { NumberField } from '@/components/forms/formik'
import { Button } from '@/components/buttons/Button'
import { FormField } from '@/components/forms/FormField'
import { TextArea } from '@/components/forms/TextArea'

import { getMissingPromptTokens } from '../helpers'

// Commit to Formik shortly after typing stops (and immediately on blur). Writing
// to Formik on every keystroke would re-render the whole editor tree (the
// draggable field rows, per-row selects, tooltips), so edits stay local until
// then — mirrors PromptDescriptionField.
const COMMIT_DEBOUNCE_MS = 250

const DebouncedTextArea = ({ value, onCommit, testId, readOnly }) => {
  const [draft, setDraft] = useState(value ?? '')
  const isFocused = useRef(false)
  const debounceId = useRef(null)

  // Accept external changes (flow load, restore) unless the user is mid-edit. A
  // read-only box can hold focus while its text is scrolled, so it never counts
  // as mid-edit.
  useEffect(() => {
    if (value !== draft && (readOnly || !isFocused.current)) {
      setDraft(value ?? '')
    }
  }, [value, readOnly])

  useEffect(() => () => clearTimeout(debounceId.current), [])

  const commit = (next) => {
    clearTimeout(debounceId.current)
    if (next !== value) {
      onCommit(next)
    }
  }

  const handleChange = (event) => {
    const next = event.target.value
    setDraft(next)
    clearTimeout(debounceId.current)
    debounceId.current = setTimeout(() => commit(next), COMMIT_DEBOUNCE_MS)
  }

  return (
    <TextArea
      rows={4}
      value={draft}
      onFocus={() => {
        isFocused.current = true
      }}
      onChange={handleChange}
      onBlur={() => {
        isFocused.current = false
        commit(draft)
      }}
      readOnly={readOnly}
      data-testid={testId}
    />
  )
}

// Balto-admin (org 1) prompt override editor shown beside a model selector.
// Hidden until the "Edit Prompt" button is clicked. Binds to the backing prompt
// record's fields (system_prompt / user_prompt / max_response_tokens) at the
// form root (namePrefix omitted) or under a specific prompt_fields row.
//
// The prompt must reference the required tokens ($bit_json, $transcript) across
// the system/user prompts; when `required` and any are missing, the fields
// border red and an error lists them by the toggle (the parent Save button uses
// hasPromptMissingRequiredTokens to block saving).
//
// `readOnly` keeps the prompt readable but uneditable — how an older
// configuration version renders. Read-only rather than disabled so a long prompt
// can still be scrolled, selected, and dragged taller (see
// PromptDescriptionField for why disabling forfeits all three).
export const PromptEditorFields = ({
  namePrefix = '',
  testId = 'rtn-prompt-editor',
  required = true,
  readOnly = false,
}) => {
  const [expanded, setExpanded] = useState(false)
  const { values, setFieldValue } = useFormikContext()
  const fieldName = (name) => (namePrefix ? `${namePrefix}.${name}` : name)

  const systemPrompt = get(values, fieldName('system_prompt'))
  const userPrompt = get(values, fieldName('user_prompt'))
  const missingTokens = required ? getMissingPromptTokens(systemPrompt, userPrompt) : []
  const isInvalid = !isEmpty(missingTokens)
  // Nothing here can be edited on an older version, so the toggle offers a look
  // at the prompt rather than an edit.
  const collapsedLabel = readOnly ? 'View Prompt' : 'Edit Prompt'

  return (
    <div className="rtn-prompt-editor">
      <div className="rtn-prompt-editor__toggle-row">
        <Button
          basic
          small
          type="button"
          className="rtn-prompt-editor__toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((previous) => !previous)}
          data-testid={`${testId}-toggle`}
        >
          <IconChevronRight className={classNames('rtn-prompt-editor__chevron', { expanded })} />
          {expanded ? 'Hide Prompt' : collapsedLabel}
        </Button>
        {isInvalid && (
          <span className="status-critical" data-testid={`${testId}-error`}>
            Include {missingTokens.join(' and ')} in the system or user prompt
          </span>
        )}
      </div>

      {expanded && (
        <div
          className={classNames('rtn-prompt-editor__body', 'field-stack', {
            'rtn-prompt-editor__body--invalid': isInvalid,
          })}
          data-testid={testId}
        >
          <FormField>
            <label>System Prompt</label>
            <DebouncedTextArea
              value={systemPrompt}
              onCommit={(next) => setFieldValue(fieldName('system_prompt'), next)}
              testId={`${testId}-system-prompt`}
              readOnly={readOnly}
            />
          </FormField>

          <FormField>
            <label>User Prompt</label>
            <DebouncedTextArea
              value={userPrompt}
              onCommit={(next) => setFieldValue(fieldName('user_prompt'), next)}
              testId={`${testId}-user-prompt`}
              readOnly={readOnly}
            />
          </FormField>

          <FormField>
            <label>Max Response Tokens</label>
            <Field
              name={fieldName('max_response_tokens')}
              component={NumberField}
              data-testid={`${testId}-max-response-tokens`}
              disabled={readOnly}
            />
          </FormField>
        </div>
      )}
    </div>
  )
}
